import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { useAiOff } from "../api/consents";

import { AICoach } from "../components/AICoach";
import { WorkoutLogModal } from "../components/coach/WorkoutLogModal";
import type { CoachRouteState } from "../components/coach/CoachDock";

/**
 * The coach page. A page in the shell, not an overlay (Coach v3): the rail is
 * still there, and Layout gives this route a fixed-viewport <main> so the
 * message list is the single scroll region. The page's own chrome is one row —
 * an eyebrow and two controls — which AICoach renders in the message column.
 *
 * DevSessionBadge is deliberately NOT mounted here: Layout already supplies it
 * on every route inside the shell, and a second one would render on top of the
 * first. (It used to be mounted here, back when /coach sat outside Layout.)
 *
 * Manual workout logging still lives in WorkoutLogModal; its trigger moved
 * from the deleted briefing strip to the coach's own WorkoutLogPill follow-up,
 * which dispatches the same `coach:open-log` event.
 */
export default function Coach() {
  const navigate = useNavigate();
  const location = useLocation();
  const draft = (location.state as CoachRouteState | null)?.draft ?? null;
  const queryClient = useQueryClient();
  // Without the AI consent the page shuts its composer and sends nothing (#399, CS-D11).
  const aiOff = useAiOff();

  // The server refused a turn for want of the consent while `me` still said it was held (it was withdrawn
  // on another device): ask `me` again, which is what the dock and the dashboard read. The only query
  // this changes.
  const handleConsentRefused = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["me"] });
  }, [queryClient]);

  // Strip the draft out of history the moment it has been sent. AICoach guards
  // against a double send on its own, but leaving the state in place would
  // resurrect the message on a reload, which is a different bug.
  const handleInitialMessageSent = useCallback(() => {
    navigate("/coach", { replace: true, state: null });
  }, [navigate]);

  // "×" is back-to-the-Dashboard, not close-the-overlay: V3 draws it as a link
  // to the Dashboard, so it goes to one fixed place regardless of how the user
  // got here. That also removes the deep-link edge case the previous
  // history-index version existed to handle.
  const handleClose = useCallback(() => {
    navigate("/");
  }, [navigate]);

  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
      style={{ color: "var(--text-cream)", fontFamily: "var(--font-body)" }}
    >
      <AICoach
        onClose={handleClose}
        initialMessage={draft}
        onInitialMessageSent={handleInitialMessageSent}
        aiOff={aiOff}
        onConsentRefused={handleConsentRefused}
      />
      <WorkoutLogModal />
    </div>
  );
}
